// src/modules/socialhive/mobile/MobileMyActivitiesPage.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import "./MobileMyActivitiesPage.css";
import { useLanguage } from "../../LanguageProvider";
import Services from "../../services/Services";
import { GeneralContext } from "../../GeneralProvider";
import {
  TbArrowLeft,
  TbCalendarEvent,
  TbMapPin,
  TbTicket,
  TbClock
} from "react-icons/tb";

function getSessionToken() {
  const rememberMe = localStorage.getItem("rememberMe") === "true";
  const localToken = localStorage.getItem("token");
  const sessionToken = sessionStorage.getItem("token");
  return rememberMe && localToken ? localToken : sessionToken || null;
}

const MONTH_KEYS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

const WEEKDAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

function formatEventDay(d, getTranslation) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const weekdayKey = WEEKDAY_KEYS[d.getDay()];
  const monthKey = MONTH_KEYS[d.getMonth()];
  const dayOfMonth = d.getDate();
  return `${getTranslation(weekdayKey)}, ${dayOfMonth} ${getTranslation(
    monthKey
  )}`;
}

function formatTime24(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(
    d.getMinutes()
  ).padStart(2, "0")}`;
}

function formatDateTime(d, getTranslation) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const date = formatEventDay(d, getTranslation);
  const t = formatTime24(d);
  return `${date} • ${t}`;
}

// GeneralProvider + fallback: local/session storage
function getUserIdFromContextOrStorage(ctxUser) {
  if (ctxUser) {
    return ctxUser.userID || ctxUser.userId || ctxUser.id || null;
  }

  try {
    const storedUser =
      JSON.parse(localStorage.getItem("user") || "null") ||
      JSON.parse(sessionStorage.getItem("user") || "null") ||
      null;
    return storedUser?.userID || storedUser?.userId || storedUser?.id || null;
  } catch {
    return null;
  }
}

export default function MobileMyActivitiesPage({ onBack }) {
  const { getTranslation } = useLanguage();
  const { user } = useContext(GeneralContext) || {};
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tickets, setTickets] = useState([]);
  const [openId, setOpenId] = useState(null); // aynı anda sadece bir bilet açık

  useEffect(() => {
    const token = getSessionToken();
    const userID = getUserIdFromContextOrStorage(user);

    if (!token || !userID) {
      setError(
        getTranslation("mustBeLoggedIn") ||
          "Bu sayfayı görebilmek için giriş yapmalısın."
      );
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    Services.SocialHiveGetMyAllTickets(token, userID)
      .then((res) => {
        if (res?.data?.status && Array.isArray(res.data.result)) {
          setTickets(res.data.result);
        } else {
          setTickets([]);
        }
      })
      .catch(() => {
        setError(getTranslation("somethingWentWrong") || "Something went wrong");
        setTickets([]);
      })
      .finally(() => setLoading(false));
  }, [getTranslation, user]);

  const normalizedTickets = useMemo(() => {
    if (!Array.isArray(tickets)) return [];

    return tickets.map((t) => {
      const eventStartTs = t.eventStartDateTime
        ? parseInt(t.eventStartDateTime, 10) * 1000
        : null;
      const eventEndTs = t.eventEndDateTime
        ? parseInt(t.eventEndDateTime, 10) * 1000
        : null;
      const paymentTs = t.paymentDateTime
        ? parseInt(t.paymentDateTime, 10) * 1000
        : null;

      const eventStartDate =
        eventStartTs && Number.isFinite(eventStartTs)
          ? new Date(eventStartTs)
          : null;
      const eventEndDate =
        eventEndTs && Number.isFinite(eventEndTs)
          ? new Date(eventEndTs)
          : null;
      const paymentDate =
        paymentTs && Number.isFinite(paymentTs) ? new Date(paymentTs) : null;

      const eventDateText = eventStartDate
        ? formatEventDay(eventStartDate, getTranslation)
        : "";
      const timeRangeText =
        eventStartDate && eventEndDate
          ? `${formatTime24(eventStartDate)} - ${formatTime24(eventEndDate)}`
          : "";

      const purchaseDateText = paymentDate
        ? formatDateTime(paymentDate, getTranslation)
        : "";

      return {
        id: t.id,
        eventTitle: t.eventTitle || "-",
        cover: t.cover || "",
        isOnline: t.isOnline === "1",
        location: t.locationAddress || "",
        eventDateText,
        timeRangeText,
        purchaseDateText,
        type: t.type || "",
        ticketQRCode: t.ticketQRCode || "",
        isUsed: t.isUsed === "1",
        raw: t,
      };
    });
  }, [tickets, getTranslation]);

  // İlk yüklemede: ilk bilet açık olsun
  useEffect(() => {
    if (!normalizedTickets.length) return;
    if (openId == null) {
      setOpenId(normalizedTickets[0].id);
    }
  }, [normalizedTickets, openId]);

  return (
    <div className="mle-activities">
      <div className="mle-activities-header">
        <button
          type="button"
          className="mle-activities-back"
          onClick={onBack}
        >
          <TbArrowLeft size={18} />
          <span>{getTranslation("back") || "Back"}</span>
        </button>
        <h1 className="mle-activities-title">
          {getTranslation("myActivities") || "My Activities"}
        </h1>
      </div>

      {loading && (
        <div className="mle-activities-state">
          <div className="mle-activities-skeleton" />
          <div className="mle-activities-skeleton" />
          <div className="mle-activities-skeleton" />
        </div>
      )}

      {!loading && error && (
        <div className="mle-activities-state mle-activities-error">
          {error}
        </div>
      )}

      {!loading && !error && normalizedTickets.length === 0 && (
        <div className="mle-activities-state">
          {getTranslation("noActivitiesYet") ||
            "Henüz herhangi bir biletin bulunmuyor."}
        </div>
      )}

      {!loading && !error && normalizedTickets.length > 0 && (
        <div className="mle-activities-list">
          {normalizedTickets.map((item) => {
            const isOpen =
              openId != null
                ? openId === item.id
                : normalizedTickets[0]?.id === item.id;

            return (
              <div
                key={item.id}
                className={
                  "mle-ticket-card" + (isOpen ? " mle-ticket-card--open" : "")
                }
              >
                <button
                  type="button"
                  className="mle-ticket-cover-btn"
                  onClick={() => {
                    // aynı anda sadece bir açık olsun, aynı karta tıklarsa kapat
                    setOpenId((prev) =>
                      prev === item.id ? null : item.id
                    );
                  }}
                >
                  <div className="mle-ticket-cover-wrap">
                    {item.cover && (
                      <img
                        className="mle-ticket-cover-img"
                        src={item.cover}
                        alt={item.eventTitle}
                      />
                    )}

                    {/* USED watermark */}
                    {item.isUsed && (
                      <div className="mle-ticket-used-watermark">
                        USED
                      </div>
                    )}

                    {/* Alt overlay: event title */}
                    <div className="mle-ticket-cover-overlay">
                      <span className="mle-ticket-cover-title">
                        {item.eventTitle}
                      </span>
                    </div>
                  </div>
                </button>

                {isOpen && (
                  <div className="mle-ticket-details">
                    {/* Event date */}
                    {item.eventDateText && (
                      <div className="mle-ticket-row">
                        <div className="mle-ticket-row-label">
                          <TbCalendarEvent size={16} />
                          <span>
                            {getTranslation("eventDate") || "Event date"}
                          </span>
                        </div>
                        <div className="mle-ticket-row-value">
                          {item.eventDateText}
                        </div>
                      </div>
                    )}

                    {/* Event time range */}
                    {item.timeRangeText && (
                      <div className="mle-ticket-row">
                        <div className="mle-ticket-row-label">
                          <TbClock size={16} />
                          <span>
                            {getTranslation("timeRange") || "Event time"}
                          </span>
                        </div>
                        <div className="mle-ticket-row-value">
                          {item.timeRangeText}
                        </div>
                      </div>
                    )}

                    {/* Address */}
                    {item.location && (
                      <div className="mle-ticket-row">
                        <div className="mle-ticket-row-label">
                          <TbMapPin size={16} />
                          <span>
                            {getTranslation("address") || "Address"}
                          </span>
                        </div>
                        <div className="mle-ticket-row-value">
                          {item.location}
                        </div>
                      </div>
                    )}

                    {/* Purchase date */}
                    {item.purchaseDateText && (
                      <div className="mle-ticket-row">
                        <div className="mle-ticket-row-label">
                          <TbTicket size={16} />
                          <span>
                            {getTranslation("purchaseDate") || "Purchase date"}
                          </span>
                        </div>
                        <div className="mle-ticket-row-value">
                          {item.purchaseDateText}
                        </div>
                      </div>
                    )}

                    {/* Ticket type */}
                    {item.type && (
                      <div className="mle-ticket-row">
                        <div className="mle-ticket-row-label">
                          <TbTicket size={16} />
                          <span>
                            {getTranslation("ticketType") || "Ticket type"}
                          </span>
                        </div>
                        <div className="mle-ticket-row-value">
                          {item.type}
                        </div>
                      </div>
                    )}

                    {/* QR */}
                    {item.ticketQRCode && (
                      <div className="mle-ticket-row mle-ticket-row-qr">
                        <div className="mle-ticket-row-label">
                          <TbTicket size={16} />
                          <span>
                            {getTranslation("QR") || "QR code"}
                          </span>
                        </div>
                        <div className="mle-ticket-row-value">
                          <img
                            className="mle-ticket-qr-img"
                            src={item.ticketQRCode}
                            alt="Ticket QR"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

